import { fromJS } from 'immutable';
import { jwtDecode } from 'jwt-decode';
import identity from 'lodash/identity';
import { createSelector } from 'reselect';

import { currentJwt } from '@src/utils/jwtHelpers';

export const types = {
    USER_INFO_SET: 'USER_INFO_SET',
};

const initialState = fromJS({
    uuid: null,
    name: null,
    email: null,
    agentCode: null,
    producerId: null,
    accessLevel: null,
    limitedPremierAccess: null,
    activePremierAccess: null,
    premierAccessGroup: null,
    premierAccessRole: null,
    premierAccessLevel: null,
    salesChannel: null,
    exp: null,
    permissions: null,
    agency: null,
    premierBannerEnabled: null,
    pilot: null,
});

const userInfo = (state = initialState, action) => {
    switch (action.type) {
    case types.USER_INFO_SET: {
        const info = {};

        if (action.payload) {
            const { payload } = action;
            info.uuid = payload.uuid;
            info.name = payload.name;
            info.email = payload.email;
            info.agentCode = payload.agentCode;
            info.producerId = payload.producerId;
            info.employeeId = payload.employeeId;
            info.accessLevel = payload.accessLevel;
            info.limitedPremierAccess = payload.limitedPremierAccess;
            info.activePremierAccess = payload.activePremierAccess;
            info.premierAccessGroup = payload.premierAccessGroup;
            info.premierAccessRole = payload.premierAccessRole;
            info.premierAccessLevel = payload.premierAccessLevel;
            info.salesChannel = payload.salesChannel;
            info.exp = payload.exp;
            info.permissions = payload.permissions;
            info.agency = payload.agency;
            info.premierBannerEnabled = payload.premierBannerEnabled;
            info.pilot = payload.pilot;
        } else {
            const jwt = currentJwt();
            let decodedJWT = null;
            if (jwt) {
                decodedJWT = jwtDecode(jwt);
            }

            if (decodedJWT) {
                info.uuid = decodedJWT.uuid;
                info.exp = decodedJWT.exp;

                // Legacy token check - if it has more data, use it.
                if (decodedJWT.access_level !== undefined) {
                    info.name = decodedJWT.name;
                    info.email = decodedJWT.email;
                    info.agentCode = decodedJWT.agent_code;
                    info.producerId = decodedJWT.producer_id;
                    info.employeeId = decodedJWT.employee_id;
                    info.accessLevel = decodedJWT.access_level;
                    info.limitedPremierAccess = decodedJWT.limited_premier_access;
                    info.activePremierAccess = decodedJWT.active_premier_access;
                    info.premierAccessGroup = decodedJWT.premier_access_group ? JSON.parse(decodedJWT.premier_access_group) : null;
                    info.premierAccessRole = decodedJWT.premier_access_role;
                    info.premierAccessLevel = decodedJWT.premier_access_level;
                    info.salesChannel = decodedJWT.sales_channel;
                    info.permissions = decodedJWT.permissions;
                    info.agency = decodedJWT.agency ? JSON.parse(decodedJWT.agency) : null;
                    info.premierBannerEnabled = decodedJWT.premier_banner_enabled;
                    info.pilot = decodedJWT.pilot;
                }
            }
        }

        return state.merge(info);
    }

    default:
        return state;
    }
};

const getUserInfo = createSelector(identity, (state) => state.getIn(['app', 'userInfo']));
const getEmployeeId = createSelector(getUserInfo, (userInfo) => userInfo.get('employeeId'));
const getAccessLevel = createSelector(getUserInfo, (userInfo) => userInfo.get('accessLevel'));
const getSalesChannel = createSelector(getUserInfo, (userInfo) => userInfo.get('salesChannel'));
const getExp = createSelector(getUserInfo, (userInfo) => userInfo.get('exp'));
const getLimitedPremierAccess = createSelector(getUserInfo, (userInfo) => userInfo.get('limitedPremierAccess'));
const getActivePremierAccess = createSelector(getUserInfo, (userInfo) => userInfo.get('activePremierAccess'));
const getPremierAccessGroup = createSelector(getUserInfo, (userInfo) => userInfo.get('premierAccessGroup'));
const getPremierAccessRole = createSelector(getUserInfo, (userInfo) => userInfo.get('premierAccessRole'));
const getPremierAccessLevel = createSelector(getUserInfo, (userInfo) => userInfo.get('premierAccessLevel'));
const getPermissions = createSelector(getUserInfo, (userInfo) => userInfo.get('permissions'));
const getIsPilot = createSelector(getUserInfo, (userInfo) => userInfo.get('pilot'));

const selectors = {
    getUserInfo,
    getEmployeeId,
    getAccessLevel,
    getSalesChannel,
    getExp,
    getPermissions,
    getLimitedPremierAccess,
    getActivePremierAccess,
    getPremierAccessGroup,
    getPremierAccessRole,
    getPremierAccessLevel,
    getIsPilot,
};

export { userInfo as default, selectors };
